import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getTeachersBySchoolId,
  getCurrentAlpacaLead,
  getAlpacaLeadChangeReasonOptions,
  addAlpacaLeadRole,
  removeAlpacaLeadRole,
  updateAlpacaLeadChangeReason,
  whoami
} from "../services/APIClient";

function ChangeAlpacaLeadModal({ show, onHide, schoolid }) {
  const [teachers, setTeachers] = useState([]); // Store teachers
  const [currentLead, setCurrentLead] = useState(null); // Store current ALPACA lead
  const [multipleLeadsError, setMultipleLeadsError] = useState(null); // Store multiple leads error
  const [selectedTeacher, setSelectedTeacher] = useState(""); // Store the selected new lead
  const [selectedReason, setSelectedReason] = useState(""); // Store selected reason
  const [otherReason, setOtherReason] = useState(""); // Store custom reason text
  const [reasonOptions, setReasonOptions] = useState([]); // Store reason options
  const [confirmationVisible, setConfirmationVisible] = useState(false); // Toggle for confirmation
  const [statusMessage, setStatusMessage] = useState(""); // Status message for success/failure
  const [alertVariant, setAlertVariant] = useState(""); // Alert variant for Bootstrap
  const [loading, setLoading] = useState(false); // Loading state
  const [currentUserId, setCurrentUserId] = useState(null); // Current user ID

  const notifySuccess = (msg) => toast.success(msg);
  const notifyError = (msg) => toast.error(msg);

  // Fetch all required data when modal opens
  useEffect(() => {
    if (schoolid && show) {
      setLoading(true);
      Promise.all([
        fetchTeachers(),
        fetchCurrentLead(),
        fetchReasonOptions(),
        fetchCurrentUser()
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [schoolid, show]);

  const fetchTeachers = async () => {
    try {
      const response = await getTeachersBySchoolId(schoolid);
      setTeachers(response);
    } catch (error) {
      setStatusMessage("Error fetching teachers. Please try again.");
      setAlertVariant("danger");
    }
  };

  const fetchCurrentLead = async () => {
    try {
      const response = await getCurrentAlpacaLead(schoolid);
      console.log("Current lead response:", response);
      
      if (response && response.error === 'Multiple ALPACA Leads found') {
        // Handle multiple leads scenario
        setMultipleLeadsError(response);
        setCurrentLead(null);
        setStatusMessage(`Data integrity issue: ${response.message}`);
        setAlertVariant("danger");
      } else {
        // Normal case: single lead or no lead
        setCurrentLead(response);
        setMultipleLeadsError(null);
      }
    } catch (error) {
      console.error("Error fetching current lead:", error);
      setCurrentLead(null);
      setMultipleLeadsError(null);
    }
  };

  const fetchReasonOptions = async () => {
    try {
      console.log("Fetching reason options...");
      const reasons = await getAlpacaLeadChangeReasonOptions();
      console.log("Reason options received:", reasons);
      setReasonOptions(reasons);
    } catch (error) {
      console.error("Error fetching reason options:", error);
      setStatusMessage("Error fetching change reasons. Please try again.");
      setAlertVariant("danger");
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await whoami();
      setCurrentUserId(response.data.stakeholder.id);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  // Handle removing ALPACA Lead role from a specific teacher
  const handleRemoveLeadRole = async (teacherId, teacherName) => {
    setLoading(true);
    try {
      await removeAlpacaLeadRole(schoolid, teacherId);
      
      notifySuccess(`Removed ALPACA Lead role from ${teacherName}`);
      
      // Refresh the current lead data
      await fetchCurrentLead();
      
    } catch (error) {
      console.error("Error removing ALPACA lead role:", error);
      notifyError(`Failed to remove ALPACA Lead role from ${teacherName}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle change lead confirmation
  const handleChangeAlpacaLead = () => {
    if (!selectedTeacher || !selectedReason) {
      notifyError("Please select a teacher and a reason for the change.");
      return;
    }

    if (!multipleLeadsError && selectedTeacher === currentLead?.id) {
      setStatusMessage("The selected teacher is already the current ALPACA lead.");
      setAlertVariant("warning");
      return;
    }

    const otherReasonOption = reasonOptions.find(option => option.reason === "Other");
    if (selectedReason === otherReasonOption?.id && !otherReason) {
      notifyError("Please provide a reason for the change.");
      return;
    }

    setConfirmationVisible(true);
  };

  // Handle the actual update API call after confirmation
  const confirmChangeAlpacaLead = async () => {
    if (!selectedTeacher || !selectedReason) return;

    setLoading(true);
    try {
      // Step 1: Remove ALPACA Lead role from ALL current leads (handles multiple leads scenario)
      if (multipleLeadsError) {
        // Remove from all multiple leads
        for (const lead of multipleLeadsError.multipleLeads) {
          await removeAlpacaLeadRole(schoolid, lead.id);
        }
      } else if (currentLead && currentLead.id) {
        // Remove from single current lead
        await removeAlpacaLeadRole(schoolid, currentLead.id);
      }

      // Step 2: Add new ALPACA Lead role
      await addAlpacaLeadRole(schoolid, selectedTeacher);

      // Step 3: Create history record
      const otherReasonOption = reasonOptions.find(option => option.reason === "Other");
      const reasonText = selectedReason === "data-cleanup" 
        ? "Data Cleanup - Multiple Leads Found"
        : selectedReason === otherReasonOption?.id
          ? otherReason
          : reasonOptions.find(option => option.id === selectedReason)?.reason;

      const alpacaLeadChangeData = {
        teacherId: currentUserId,
        oldAlpacaLead: multipleLeadsError 
          ? `Multiple: ${multipleLeadsError.multipleLeads.map(l => l.name).join(', ')}`
          : currentLead?.id || "None",
        newAlpacaLead: selectedTeacher,
        reasonForChangeId: selectedReason === "data-cleanup" ? "data-cleanup" : selectedReason,
        alpacaleadchangereason: reasonText,
        dateChanged: new Date().toISOString(),
      };

      await updateAlpacaLeadChangeReason(alpacaLeadChangeData);

      const newLeadName = teachers.find(t => t.id === selectedTeacher)?.name;
      const oldLeadName = multipleLeadsError 
        ? `Multiple leads (${multipleLeadsError.multipleLeads.length})`
        : currentLead?.name || "No previous lead";

      setStatusMessage(`ALPACA lead successfully ${multipleLeadsError ? 'resolved and assigned' : 'changed'} from "${oldLeadName}" to "${newLeadName}"!`);
      setAlertVariant("success");
      notifySuccess(multipleLeadsError ? "Multiple leads resolved and ALPACA Lead assigned successfully!" : "ALPACA Lead updated successfully!");

      // Update current lead in state
      const newLead = teachers.find(t => t.id === selectedTeacher);
      setCurrentLead(newLead);
      setMultipleLeadsError(null);

      clearFields();
      autoClearMessage();
    } catch (error) {
      console.error("Error updating ALPACA Lead:", error);
      setStatusMessage("Failed to change ALPACA lead. Please try again.");
      setAlertVariant("danger");
      notifyError("Failed to update ALPACA Lead. Please try again.");
    } finally {
      setConfirmationVisible(false);
      setLoading(false);
    }
  };

  const handleReasonChange = (e) => {
    setSelectedReason(e.target.value);
    const otherReasonOption = reasonOptions.find(option => option.reason === "Other");
    if (e.target.value !== otherReasonOption?.id) {
      setOtherReason("");
    }
  };

  // Automatically clear the status message after a few seconds
  const autoClearMessage = () => {
    setTimeout(() => {
      setStatusMessage("");
      setAlertVariant("");
    }, 5000); // Clear message after 5 seconds
  };

  // Clear the modal fields and messages
  const clearFields = () => {
    setSelectedTeacher("");
    setSelectedReason("");
    setOtherReason("");
  };

  // Clear all state when modal closes
  const handleModalClose = () => {
    onHide();
    clearFields();
    setConfirmationVisible(false);
    setStatusMessage("");
    setAlertVariant("");
  };

  return (
    <Modal show={show} onHide={handleModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>Change ALPACA Lead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <Alert variant="info">Loading school information...</Alert>
        )}

        {/* Display multiple leads - allow CS to resolve */}
        {multipleLeadsError && (
          <Alert variant="warning" className="mb-3">
            <Alert.Heading>⚠️ Multiple ALPACA Leads Detected</Alert.Heading>
            <p><strong>Found {multipleLeadsError.multipleLeads.length} teachers with ALPACA Lead role:</strong></p>
            <ul className="mb-3">
              {multipleLeadsError.multipleLeads.map((lead, index) => (
                <li key={lead.id}>
                  <strong>{lead.name}</strong> ({lead.email || 'No email'})
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    className="ms-2"
                    onClick={() => handleRemoveLeadRole(lead.id, lead.name)}
                    disabled={loading}
                  >
                    Remove ALPACA Lead Role
                  </Button>
                </li>
              ))}
            </ul>
            <p className="mb-0">
              <strong>CS Action:</strong> Remove the ALPACA Lead role from the incorrect teachers above, then assign it to the correct teacher below.
            </p>
          </Alert>
        )}

        {/* Display current ALPACA lead */}
        {!multipleLeadsError && (
          <div className="mb-3">
            <Form.Label><strong>Current ALPACA Lead:</strong></Form.Label>
            <div className="d-flex align-items-center">
              {currentLead ? (
                <>
                  <span className="me-2">{currentLead.name}</span>
                  <Badge bg="primary">Current Lead</Badge>
                </>
              ) : (
                <span className="text-muted">No ALPACA lead assigned</span>
              )}
            </div>
          </div>
        )}

        {/* Dropdown for selecting a new lead - always available for CS */}
        <Form.Group className="mb-3">
          <Form.Label>
            {multipleLeadsError ? "Assign ALPACA Lead to:" : "Select New ALPACA Lead:"}
          </Form.Label>
          <Form.Control
            as="select"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            disabled={loading}
          >
            <option value="">Select a teacher</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.name}
                {teacher.id === currentLead?.id ? " (Current Lead)" : ""}
                {multipleLeadsError && multipleLeadsError.multipleLeads.some(lead => lead.id === teacher.id) ? " (Has Lead Role)" : ""}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Reason selection - always available for CS */}
        <Form.Group className="mb-3">
          <Form.Label>Select a reason for the change:</Form.Label>
          <Form.Control
            as="select"
            value={selectedReason}
            onChange={handleReasonChange}
            disabled={loading}
          >
            <option value="">Select a reason</option>
            {multipleLeadsError && <option value="data-cleanup">Data Cleanup - Multiple Leads Found</option>}
            {reasonOptions.length === 0 && !loading && (
              <option value="" disabled>No reasons available</option>
            )}
            {reasonOptions.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.reason}
              </option>
            ))}
          </Form.Control>
          {reasonOptions.length === 0 && !loading && (
            <small className="text-muted">
              Debug: Reason options count: {reasonOptions.length}
            </small>
          )}
        </Form.Group>

        {/* Other reason input */}
        {selectedReason === reasonOptions.find(option => option.reason === "Other")?.id && (
          <Form.Group className="mb-3">
            <Form.Label><strong>Other Reason:</strong></Form.Label>
            <Form.Control
              type="text"
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Please specify the reason"
              disabled={loading}
              style={{
                minHeight: '38px',
                fontSize: '1rem',
                padding: '0.375rem 0.75rem'
              }}
            />
          </Form.Group>
        )}

        {/* Change lead button */}
        {selectedTeacher && selectedReason && (
          <Button 
            variant="primary" 
            className="mb-3" 
            onClick={handleChangeAlpacaLead}
            disabled={loading}
          >
            {multipleLeadsError ? "Resolve Multiple Leads & Assign to" : "Change ALPACA Lead to"} {teachers.find(t => t.id === selectedTeacher)?.name}
          </Button>
        )}

        {/* Confirmation message */}
        {confirmationVisible && (
          <div className="mb-3">
            <Alert variant="warning">
              {multipleLeadsError ? (
                <>
                  Are you sure you want to <strong>remove ALPACA Lead role from ALL current leads</strong> and assign it to "
                  {teachers.find(t => t.id === selectedTeacher)?.name}"?
                  <br />
                  <strong>Current leads to be removed:</strong> {multipleLeadsError.multipleLeads.map(l => l.name).join(', ')}
                </>
              ) : (
                <>
                  Are you sure you want to change the ALPACA lead from "
                  {currentLead?.name || "No current lead"}" to "
                  {teachers.find(t => t.id === selectedTeacher)?.name}"?
                </>
              )}
              <br />
              <strong>Reason:</strong> {
                selectedReason === "data-cleanup"
                  ? "Data Cleanup - Multiple Leads Found"
                  : selectedReason === reasonOptions.find(option => option.reason === "Other")?.id 
                    ? otherReason 
                    : reasonOptions.find(option => option.id === selectedReason)?.reason
              }
              <br />
              <small className="text-muted">
                This will {multipleLeadsError ? 'clean up the data inconsistency and ' : ''}transfer all ALPACA lead responsibilities to the new teacher and update their status in HubSpot.
              </small>
            </Alert>
            <div className="d-flex gap-2">
              <Button 
                variant="primary" 
                onClick={confirmChangeAlpacaLead}
                disabled={loading}
              >
                {multipleLeadsError ? "Resolve & Assign" : "Confirm Change"}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setConfirmationVisible(false)}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Status message for success or failure */}
        {statusMessage && (
          <Alert 
            variant={alertVariant} 
            className="mb-3" 
            dismissible 
            onClose={() => setStatusMessage("")}
          >
            {statusMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose} disabled={loading}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ChangeAlpacaLeadModal;